import { CheckBox } from '@components/ui/checkbox';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useCategoriesInfinite } from '@framework/categories';
import { IoChevronDown, IoCloseOutline } from 'react-icons/io5';
import SearchIcon from '@components/icons/search-icon';

export const CategoryFilter = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { pathname, query } = router;
  const [isOpen, setIsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading } = useCategoriesInfinite({
    limit: showAll || debouncedSearch ? 999 : 5,
    parent: null,
    text: debouncedSearch || undefined,
  });

  const selectedCategories = query?.category
    ? (query.category as string).split(',')
    : [];
  const [formState, setFormState] = useState<string[]>(selectedCategories);

  useEffect(() => {
    setFormState(selectedCategories);
  }, [query?.category]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  function handleItemClick(e: React.FormEvent<HTMLInputElement>): void {
    const { value } = e.currentTarget;
    let currentFormState = formState.includes(value)
      ? formState.filter((i) => i !== value)
      : [...formState, value];
    const { category, ...restQuery } = query;
    router.push(
      {
        pathname,
        query: {
          ...restQuery,
          ...(!!currentFormState.length
            ? { category: currentFormState.join(',') }
            : {}),
        },
      },
      undefined,
      { scroll: false },
    );
  }

  return (
    <div className="block mb-2">
      <h3
        className="text-[#333333] text-sm md:text-lg mb-5 flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{t('text-category')}</span>
        <IoChevronDown
          className={`transition-transform text-[#9D9D9D] duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </h3>
      {isOpen && (
        <div className="mt-2 flex flex-col space-y">
          <div className="flex items-center border-[1px] border-[#281914] rounded-xl px-3 py-3 mb-5">
            <SearchIcon className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('text-search-categories') || 'Search categories...'}
              className="ml-2 w-full outline-none bg-transparent text-gray-600 text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <IoCloseOutline className="w-4 h-4" />
              </button>
            )}
          </div>

          {!isLoading &&
            data?.map((category) => (
              <CheckBox
                key={category.id}
                label={category.name}
                name={category.name.toLowerCase()}
                checked={formState.includes(category.slug)}
                value={category.slug}
                onChange={handleItemClick}
                className="text-[#333333]"
                hideTick
              />
            ))}

          {!isLoading && !showAll && !debouncedSearch && (data?.length ?? 0) >= 5 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-sm text-[#281914] underline pt-2 text-left"
            >
              {t('text-show-all')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
